import React, { useContext, useEffect, useRef, useImperativeHandle, useCallback } from 'react';
import { useKeyboard } from '@react-aria/interactions';
import PropTypes from '@squiz/xaccel-prop-types';
import { generateClasses } from '@squiz/xaccel-utility-functions';
import AssistedInputContext from './AssistedInputContext';
import styles from '../css/global.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AssistedInput = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    name,
    showClear,
    clearIcon,
    preloadValueFromNameParam,
    className,
    autofocus,
    onChangeValue,
    ...restProps
  } = _ref;
  const {
    id,
    assistActive,
    activeDescendant,
    inputValue,
    isPopup,
    onInputChange,
    onKeyUp,
    onKeyDown,
    registerInputRef
  } = useContext(AssistedInputContext);
  const {
    keyboardProps
  } = useKeyboard({
    onKeyUp,
    onKeyDown
  });
  const inputRef = useRef();
  useImperativeHandle(ref, () => inputRef.current); // If user passes in a forwarded ref hand back our internal ref

  useEffect(() => {
    // Send the input ref back to the context so others components can focus etc
    registerInputRef(inputRef);
  }, []);

  // Set focus if autofocus is set to true
  useEffect(() => {
    if (autofocus) {
      if (inputRef && inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, []);
  useEffect(() => {
    if (preloadValueFromNameParam) {
      try {
        const url = new URL(document.location.href);
        const param = url.searchParams.get(name);
        if (param) {
          onInputChange({
            currentTarget: {
              value: param
            }
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      }
    }
  }, []);
  useEffect(() => {
    onChangeValue(inputValue);
  }, [inputValue]);
  const handleClearInput = useCallback(() => {
    onInputChange({
      currentTarget: {
        value: ''
      }
    });
    inputRef.current.focus();
  }, []);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("input", {
      ref: inputRef,
      id: `assisted-input-${id}`,
      "aria-activedescendant": activeDescendant,
      type: "text",
      autoComplete: "off",
      role: "combobox",
      "aria-expanded": assistActive,
      "aria-haspopup": isPopup ? 'grid' : false,
      "aria-labelledby": `assisted-input-label-${id}`,
      "aria-autocomplete": "list",
      "aria-controls": `assisted-input-results-grid-${id}`,
      onChange: onInputChange,
      name: name,
      value: inputValue,
      className: generateClasses({
        block: 'assisted-input-input',
        override: className
      }, styles),
      ...keyboardProps,
      ...restProps
    }), showClear && /*#__PURE__*/_jsxs("button", {
      type: "button",
      className: generateClasses({
        block: 'assisted-input-input',
        element: 'clear',
        modifiers: {
          show: inputValue.length > 0
        },
        override: className
      }, styles),
      onClick: handleClearInput,
      children: [clearIcon, /*#__PURE__*/_jsx("span", {
        className: generateClasses({
          block: 'assisted-input-input',
          element: 'clear-label',
          override: className
        }, styles),
        children: "Clear"
      })]
    })]
  });
});
AssistedInput.propTypes = {
  name: PropTypes.string,
  showClear: PropTypes.bool,
  clearIcon: PropTypes.node,
  preloadValueFromNameParam: PropTypes.bool,
  onChangeValue: PropTypes.func,
  className: PropTypes.classNameOverride
};
AssistedInput.defaultProps = {
  name: 'query',
  showClear: false,
  clearIcon: undefined,
  preloadValueFromNameParam: false,
  onChangeValue: () => {},
  className: undefined
};
export default AssistedInput;